import { useState } from "react";
import { Trash2 } from "lucide-react";
import { makeId, saveRecord } from "../lib/db";
import { dateInputValue, nowIso } from "../lib/time";
import type { Task, TaskStatus } from "../types";
import { Button, Field, Modal } from "./ui";

export function TaskEditor({
  task,
  onClose,
  onSaved,
  onDelete
}: {
  task?: Task;
  onClose: () => void;
  onSaved: (task: Task) => void;
  onDelete?: (task: Task) => void;
}) {
  const [title, setTitle] = useState(task?.title ?? "");
  const [body, setBody] = useState(task?.body ?? "");
  const [status, setStatus] = useState<TaskStatus>(task?.status ?? "inbox");
  const [outcome, setOutcome] = useState(task?.outcome ?? "");
  const [nextAction, setNextAction] = useState(task?.nextAction ?? "");
  const [area, setArea] = useState(task?.area ?? "");
  const [dueAt, setDueAt] = useState(dateInputValue(task?.dueAt));
  const [waitingOn, setWaitingOn] = useState(task?.waitingOn ?? "");
  const [followUpAt, setFollowUpAt] = useState(task?.followUpAt ?? "");
  const [error, setError] = useState("");

  async function save() {
    setError("");
    if (!title.trim()) {
      setError("请填写任务标题。");
      return;
    }
    if (status === "waiting" && (!waitingOn.trim() || !followUpAt)) {
      setError("等待他人的事项必须填写等待对象和跟进日期。");
      return;
    }
    const timestamp = nowIso();
    const next: Task = {
      id: task?.id ?? makeId(),
      kind: "task",
      title: title.trim(),
      body: body.trim(),
      createdAt: task?.createdAt ?? timestamp,
      updatedAt: timestamp,
      revision: task?.revision ?? 0,
      deletedAt: task?.deletedAt,
      sourceIds: task?.sourceIds,
      status,
      outcome: outcome.trim(),
      nextAction: nextAction.trim(),
      area: area.trim(),
      dueAt: dueAt || undefined,
      waitingOn: status === "waiting" ? waitingOn.trim() : undefined,
      followUpAt: status === "waiting" ? followUpAt : undefined,
      sourceType: task?.sourceType ?? "manual",
      transcript: task?.transcript,
      sourceUrl: task?.sourceUrl,
      sourceTitle: task?.sourceTitle,
      completedAt:
        status === "done" ? (task?.completedAt ?? timestamp) : status === "inbox" ? undefined : task?.completedAt,
      droppedAt: status === "dropped" ? (task?.droppedAt ?? timestamp) : undefined
    };
    const saved = await saveRecord(next);
    onSaved(saved);
    onClose();
  }

  return (
    <Modal
      title={task ? "编辑待办" : "新建待办"}
      eyebrow="任务"
      onClose={onClose}
      size="large"
      footer={
        <>
          {task && onDelete && (
            <Button type="button" variant="ghost" className="footer-delete" onClick={() => onDelete(task)}>
              <Trash2 size={16} /> 删除
            </Button>
          )}
          <Button type="button" onClick={onClose}>
            取消
          </Button>
          <Button type="button" variant="primary" onClick={() => void save()}>
            保存
          </Button>
        </>
      }
    >
      <div className="editor-form">
        <Field label="任务标题" required>
          <input
            className="input input-large"
            autoFocus
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="一句话说明要处理什么"
          />
        </Field>
        <div className="form-grid three">
          <Field label="状态">
            <select className="input" value={status} onChange={(event) => setStatus(event.target.value as TaskStatus)}>
              <option value="inbox">收件箱</option>
              <option value="next">下一步</option>
              <option value="doing">进行中</option>
              <option value="waiting">等待他人</option>
              <option value="done">已完成</option>
              <option value="dropped">已放弃</option>
            </select>
          </Field>
          <Field label="领域">
            <input
              className="input"
              list="area-options"
              value={area}
              onChange={(event) => setArea(event.target.value)}
              placeholder="销售 / 供应链 / 组织"
            />
          </Field>
          <Field label="截止日期">
            <input className="input" type="date" value={dueAt} onChange={(event) => setDueAt(event.target.value)} />
          </Field>
        </div>
        <div className="form-grid">
          <Field label="预期结果" hint="什么结果发生了，这件事才算完成？">
            <textarea
              className="textarea"
              rows={3}
              value={outcome}
              onChange={(event) => setOutcome(event.target.value)}
              placeholder="例如：形成一页可执行的会议议程"
            />
          </Field>
          <Field label="下一步" hint="下一次打开时，具体先做什么？">
            <textarea
              className="textarea"
              rows={3}
              value={nextAction}
              onChange={(event) => setNextAction(event.target.value)}
              placeholder="例如：列出三个必须讨论的问题"
            />
          </Field>
        </div>
        {status === "waiting" && (
          <div className="form-grid waiting-fields">
            <Field label="等待谁" required>
              <input
                className="input"
                value={waitingOn}
                onChange={(event) => setWaitingOn(event.target.value)}
                placeholder="姓名 / 部门"
              />
            </Field>
            <Field label="什么时间跟进" required>
              <input
                className="input"
                type="date"
                value={followUpAt}
                onChange={(event) => setFollowUpAt(event.target.value)}
              />
            </Field>
          </div>
        )}
        <Field label="备注">
          <textarea
            className="textarea"
            rows={4}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            placeholder="补充背景、判断或相关记录"
          />
        </Field>
        <datalist id="area-options">
          <option value="经营决策" />
          <option value="客户与销售" />
          <option value="供应链" />
          <option value="组织与人才" />
          <option value="财务与现金" />
          <option value="个人成长" />
        </datalist>
        {error && <p className="form-error">{error}</p>}
      </div>
    </Modal>
  );
}
